import { useState, useEffect, useCallback, useRef } from 'react';
import {
  formatDateTime,
  DateRangeFilterPopover,
  MultiSelectFilterPopover,
  TextFilterPopover,
  TextIncludeExcludeFilterPopover,
  TextWithSelectFilterPopover,
  type SortDir,
} from '../../../shared/src';
import { api, AuditLog, AuditLogSortColumn, Organization } from '../api';
import { AuditLogDetailsPanel } from './AuditLogDetailsPanel';

/** Rows per page on offer; the admin-api serves up to 200. */
const PAGE_SIZES = [20, 50, 100, 200];

/** Organization filter value for platform-level rows (mirrors the admin-api). */
const NO_ORGANIZATION = 'none';

/**
 * created_at is a timestamptz the database stamps with now(); the API returns it
 * as a UTC ISO string and the table renders it with formatDateTime(), i.e. in
 * the browser's time zone. The date filter uses the same zone.
 */
const VIEWER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface Filters {
  actions: string[];
  action_search: string | null;
  organization_ids: string[];
  user_email: string | null;
  user_email_exclude: string | null;
  ip_search: string | null;
  date_from: string | null;
  date_to: string | null;
}

const defaultFilters: Filters = {
  actions: [],
  action_search: null,
  organization_ids: [],
  user_email: null,
  user_email_exclude: null,
  ip_search: null,
  date_from: null,
  date_to: null,
};

export function AuditLogList() {
  // Data state
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZES[0],
    total: 0,
    totalPages: 0,
  });

  // Filter and sort state, edited from the column headers
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sort, setSort] = useState<{ by: AuditLogSortColumn; dir: SortDir }>({ by: 'time', dir: 'desc' });
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  // Filter options
  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Row whose details are open in the side panel
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const closeDetails = useCallback(() => setSelectedLog(null), []);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [actionsRes, orgsRes] = await Promise.all([
          api.getAuditLogFilters(),
          api.listOrganizations({ limit: 1000 }),
        ]);
        setAvailableActions(actionsRes.actions);
        setOrganizations(orgsRes.organizations);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Only the latest request may fill the table: requests are not cancelled, so a
  // slower response to an earlier query (e.g. the unfiltered list, still in
  // flight when a filter is applied) would otherwise land last and show rows the
  // applied filter excludes
  const latestRequest = useRef(0);

  // Load logs function
  const loadLogs = useCallback(
    async (page = 1) => {
      const request = ++latestRequest.current;
      setLoading(true);
      try {
        const result = await api.listAuditLogs({
          page,
          limit: pageSize,
          sort_by: sort.by,
          sort_order: sort.dir,
          actions: filters.actions,
          organization_ids: filters.organization_ids,
          ...(filters.action_search && { action_search: filters.action_search }),
          ...(filters.user_email && { user_email: filters.user_email }),
          ...(filters.user_email_exclude && { user_email_exclude: filters.user_email_exclude }),
          ...(filters.ip_search && { ip_search: filters.ip_search }),
          ...(filters.date_from && { date_from: filters.date_from }),
          ...(filters.date_to && { date_to: filters.date_to }),
          tz: VIEWER_TIME_ZONE,
        });
        if (request !== latestRequest.current) return;
        setLogs(result.logs);
        setPagination(result.pagination);
      } catch (err) {
        if (request !== latestRequest.current) return;
        console.error('Failed to load audit logs:', err);
      } finally {
        if (request === latestRequest.current) setLoading(false);
      }
    },
    [filters, sort, pageSize]
  );

  // Initial load, and back to page 1 on a filter, sort or page size change
  useEffect(() => {
    loadLogs(1);
  }, [loadLogs]);

  const handlePageChange = (newPage: number) => {
    loadLogs(newPage);
  };

  const sortProps = (column: AuditLogSortColumn) => ({
    sortable: true,
    sortActive: sort.by === column,
    sortDir: sort.dir,
    onSort: (dir: SortDir) => setSort({ by: column, dir }),
  });

  const organizationOptions = [
    { value: NO_ORGANIZATION, label: 'No organization (platform)' },
    ...organizations.map((org) => ({ value: org.id, label: org.name })),
  ];

  const hasActiveFilters =
    filters.actions.length > 0 ||
    filters.organization_ids.length > 0 ||
    [
      filters.action_search,
      filters.user_email,
      filters.user_email_exclude,
      filters.ip_search,
      filters.date_from,
      filters.date_to,
    ].some(Boolean);

  return (
    <div className="page">
      <header className="page-header">
        <h1>Audit Logs</h1>
      </header>

      {/* Results summary and page size */}
      <div className="results-bar">
        <div className="results-summary">
          Showing {logs.length} of {pagination.total} logs
          {hasActiveFilters && (
            <>
              {' (filtered) · '}
              <button className="btn-link" onClick={() => setFilters(defaultFilters)}>
                Clear all filters
              </button>
            </>
          )}
        </div>
        <label className="page-size">
          Rows per page
          <select className="status-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Table: headers open each column's sort and filter popover */}
      <table className="data-table">
        <thead>
          <tr>
            <th>
              <DateRangeFilterPopover
                label="Time"
                timeZone={VIEWER_TIME_ZONE}
                allowTime
                {...sortProps('time')}
                value={{ from: filters.date_from, to: filters.date_to }}
                onChange={(v) => setFilters({ ...filters, date_from: v.from, date_to: v.to })}
              />
            </th>
            <th>
              <TextWithSelectFilterPopover
                label="Action"
                {...sortProps('action')}
                value={{ contains: filters.action_search, selected: filters.actions }}
                options={availableActions}
                onChange={(v) => setFilters({ ...filters, action_search: v.contains, actions: v.selected })}
                placeholder="e.g. login, user., folder.move"
              />
            </th>
            <th>
              <MultiSelectFilterPopover
                label="Organization"
                {...sortProps('organization')}
                value={filters.organization_ids}
                options={organizationOptions}
                onChange={(v) => setFilters({ ...filters, organization_ids: v })}
                searchable={organizationOptions.length > 10}
                searchPlaceholder="Search organizations…"
              />
            </th>
            <th>
              <TextIncludeExcludeFilterPopover
                label="User"
                {...sortProps('user')}
                value={{ contains: filters.user_email, excludes: filters.user_email_exclude }}
                onChange={(v) => setFilters({ ...filters, user_email: v.contains, user_email_exclude: v.excludes })}
                containsLabel="Email contains"
                excludesLabel="Email does not contain"
                containsPlaceholder="e.g. @acme.com"
                excludesPlaceholder="e.g. @fakto.ai"
              />
            </th>
            <th>
              <TextFilterPopover
                label="IP Address"
                align="right"
                {...sortProps('ip_address')}
                value={filters.ip_search}
                onChange={(v) => setFilters({ ...filters, ip_search: v })}
                placeholder="e.g. 203.0.113."
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="empty-state">
                Loading...
              </td>
            </tr>
          ) : (
            <>
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className={`audit-log-row ${selectedLog?.id === log.id ? 'selected' : ''}`}
                  onClick={() => setSelectedLog(log)}
                >
                  <td>{formatDateTime(log.created_at)}</td>
                  <td>
                    <code>{log.action}</code>
                  </td>
                  <td>{log.organization_name || '-'}</td>
                  <td>
                    {log.user_email ? (
                      log.user_email
                    ) : log.admin_email ? (
                      <span>
                        {log.admin_email}
                        <span className="role-badge role-admin" style={{ marginLeft: '0.5rem' }}>
                          Admin
                        </span>
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{log.ip_address || '-'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty-state">
                    {hasActiveFilters ? 'No logs match your filters' : 'No audit logs'}
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn-secondary"
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span className="page-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="btn-secondary"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {selectedLog && <AuditLogDetailsPanel log={selectedLog} onClose={closeDetails} />}
    </div>
  );
}
